import { getBackendUrl } from '../utils/api';

// Interface matching the Infrastructure component's expected structure
export interface InfrastructureData {
  url: string;
  hostname: string;
  domain: string;
  domainData: {
    registrar: string;
    registrationDate: string;
    expirationDate: string;
    nameservers: string[];
    abuseContact: string;
    whoisPrivacy: boolean;
    registrant: string;
  };
  hostingData: {
    ipAddress: string;
    asn: string;
    isp: string;
    country: string;
    city: string;
    cdn: string | null;
    reverseDns: string;
    sslStatus: string;
    ports: number[];
  };
  analytics: {
    googleAnalytics: string | null;
    googleTagManager: string | null;
    facebookPixel: string | null;
    yandexMetrica: string | null;
    hotjar: string | null;
    other: string[];
  };
  monetization: {
    paymentProcessors: string[];
    cryptoAddresses: string[];
    adNetworks: string[];
    tagManagementServices: string[];
    otherServices: string[];
    affiliatePrograms: string[];
    ecommercePlatform: string | null;
    totalExternalServices: number;
    hasInternalMonetization: boolean;
  };
  outboundLinks: {
    total: number;
    monetized: string[];
    partnerships: string[];
    suspicious: string[];
  };
  embeddedBrands: {
    logos: string[];
    knownEntities: string[];
    socialMedia: string[];
  };
  searchIndexing: {
    googleIndexed: boolean;
    bingIndexed: boolean;
    yandexIndexed: boolean;
    sitemap: string | null;
    robotsTxt: string | null;
    metaTitle: string;
    metaDescription: string;
  };
  riskScore: number;
  lastUpdated: Date;
}

export class InfrastructureService {
  private caseName: string;

  constructor(caseName: string = '27june1341') {
    this.caseName = caseName;
  }

  /**
   * Get ALL infrastructure data for a case with a single API call
   * This is the ONLY method that should call the backend - single source of truth
   */
  async getAllInfrastructureData(caseName?: string): Promise<InfrastructureData[]> {
    try {
      const caseNameToUse = caseName || this.caseName;
      console.log('🔄 [FRONTEND DEBUG] Infrastructure Service called with case:', caseNameToUse);
      
      // SINGLE API CALL - this is our one source of truth
      const response = await fetch(`${getBackendUrl()}/api/evidence-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          case_name: caseNameToUse
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const evidenceData = await response.json();
      console.log('✅ [FRONTEND DEBUG] Evidence data received, processing locally...');
      
      // 🔍 RAW DATA DUMP - Let's see exactly what's available
      console.log('🔍 [RAW API DUMP] Complete evidence data structure:');
      console.log(JSON.stringify(evidenceData, null, 2));
      
      // Extract and process ALL data locally from this single response
      const results = this.processEvidenceDataLocally(evidenceData);
      
      console.log('🎯 [FRONTEND DEBUG] Final results being returned to component:', results.length, 'items');
      if (results.length > 0) {
        const firstResult = results[0];
        console.log('📊 [FRONTEND DEBUG] First result sample data:');
        console.log('   - hostname:', firstResult.hostname);
        console.log('   - registrar:', firstResult.domainData.registrar);
        console.log('   - IP:', firstResult.hostingData.ipAddress);
        console.log('   - ISP:', firstResult.hostingData.isp);
        console.log('   - Country:', firstResult.hostingData.country);
      }
      
      return results;
      
    } catch (error) {
      console.error('❌ [FRONTEND DEBUG] Error in getAllInfrastructureData:', error);
      return [];
    }
  }

  /**
   * Process all evidence data locally without additional API calls
   */
  private processEvidenceDataLocally(evidenceResponse: any): InfrastructureData[] {
    const data = evidenceResponse.data || {};
    const results: InfrastructureData[] = [];
    
    console.log('📊 [Infrastructure Service] Processing evidence data locally...');
    
    // Step 1: Extract all domains from evidence_urls (no API call needed)
    const domains = this.extractDomainsLocally(data);
    console.log(`🔍 [Infrastructure Service] Found ${domains.length} domains:`, domains);
    
    if (domains.length === 0) {
      console.log('⚠️ [Infrastructure Service] No domains found in evidence data');
      return [];
    }
    
    // Step 2: For each domain, parse infrastructure data locally (no API calls)
    domains.forEach(domain => {
      try {
        const infrastructureData = this.parseInfrastructureDataLocally(domain, data);
        results.push(infrastructureData);
        console.log(`✅ [Infrastructure Service] Processed infrastructure data for: ${domain}`);
      } catch (error) {
        console.error(`❌ [Infrastructure Service] Error processing ${domain}:`, error);
        // Add fallback data for this domain
        results.push(this.createDefaultInfrastructureData(domain));
      }
    });
    
    console.log(`✅ [Infrastructure Service] Processed ${results.length} domains locally`);
    return results;
  }

  /**
   * Extract domains from evidence data locally (no API calls)
   */
  private extractDomainsLocally(data: any): string[] {
    const domains: string[] = [];
    
    // Extract domains from evidence_urls
    if (data.evidence_urls && Array.isArray(data.evidence_urls)) {
      data.evidence_urls.forEach((urlEntry: any) => {
        if (urlEntry.url) {
          try {
            const urlObj = new URL(urlEntry.url);
            const hostname = urlObj.hostname.replace(/^www\./, '');
            domains.push(hostname);
            console.log(`📋 [Infrastructure Service] Found domain from evidence_urls: ${hostname}`);
          } catch (error) {
            console.warn(`⚠️ [Infrastructure Service] Invalid URL in evidence: ${urlEntry.url}`);
          }
        }
      });
    }
    
    // Also check infrastructure_domains if available
    if (data.infrastructure_domains && Array.isArray(data.infrastructure_domains)) {
      data.infrastructure_domains.forEach((domain: string) => {
        if (domain && typeof domain === 'string') {
          const cleanDomain = domain.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
          domains.push(cleanDomain);
          console.log(`📋 [Infrastructure Service] Found domain from infrastructure_domains: ${cleanDomain}`);
        }
      });
    }
    
    return [...new Set(domains)]; // Remove duplicates
  }

  /**
   * Parse infrastructure data for a specific domain locally (no API calls)
   */
  private parseInfrastructureDataLocally(domain: string, data: any): InfrastructureData {
    const hostname = domain.replace(/^https?:\/\//, '').replace(/^www\./, '');
    
    const networkInfo = data.network_info || {};

    // Find the network info entry for this domain
    const networkKeys = Object.keys(networkInfo);
    let domainNetworkInfo: any = {};

    // Try to find the network info entry that matches our domain
    for (const key of networkKeys) {
      const networkEntry = networkInfo[key];
      if (networkEntry && (
        networkEntry.domain === hostname ||
        networkEntry.target_hostname === hostname ||
        networkEntry.url?.includes(hostname) ||
        key.toLowerCase().includes(hostname.replace('.', ''))
      )) {
        domainNetworkInfo = networkEntry;
        console.log(`📍 [Infrastructure Service] Found network info for ${hostname} in key: ${key}`);
        break;
      }
    }

    // If no specific match found, use the first available entry as fallback
    if (Object.keys(domainNetworkInfo).length === 0 && networkKeys.length > 0) {
      const firstKey = networkKeys[0];
      domainNetworkInfo = networkInfo[firstKey];
      console.log(`⚠️ [Infrastructure Service] Using fallback network info from key: ${firstKey}`);
    }

    const whoisData = domainNetworkInfo.whois_data || {};
    
    // ✅ Enhanced data extraction with service context
    const ipApiData = domainNetworkInfo.ip_api_data || {};
    console.log(`🔧 [SERVICE] Using ${domainNetworkInfo.service_used} - Limited to IP geolocation data`);
    
    // ✅ Extract monetization data from the CORRECT location: network_info.monetization_data
    const networkMonetizationData = domainNetworkInfo.monetization_data || {};
    
    console.log(`🔍 [MONETIZATION] Processing for ${hostname}: Found ${networkMonetizationData.ad_networks_detected?.length || 0} ad networks, ${networkMonetizationData.analytics_services?.length || 0} analytics services`);
    
    // Parse nameservers - handle service limitation
    let nameservers: string[] = [];
          if (whoisData.nameservers && whoisData.nameservers.length > 0) {
        nameservers = Array.isArray(whoisData.nameservers) ? whoisData.nameservers : [whoisData.nameservers];
      }
      
      // ✅ Enhanced location data using additional fields
      const enhancedLocation = {
        city: domainNetworkInfo.city || ipApiData.city || 'Unknown',
        region: domainNetworkInfo.region_name || ipApiData.regionName || domainNetworkInfo.region || 'Unknown', 
        country: domainNetworkInfo.country || ipApiData.country || 'Unknown',
        countryCode: domainNetworkInfo.country_code || ipApiData.countryCode || 'Unknown'
      };

    // Parse external monetization services from the ACTUAL data structure
    const adNetworks: string[] = [];
    const tagManagementServices: string[] = [];
    const otherServices: string[] = [];
    
         // Extract ad networks from ad_networks_detected array
           if (networkMonetizationData.ad_networks_detected && Array.isArray(networkMonetizationData.ad_networks_detected)) {
        networkMonetizationData.ad_networks_detected.forEach((networkName: string) => {
         if (networkName.toLowerCase().includes('tag manager')) {
           tagManagementServices.push(networkName);
         } else {
           adNetworks.push(networkName);
         }
       });
     }
     
     // Extract analytics services from analytics_services array
      if (networkMonetizationData.analytics_services && Array.isArray(networkMonetizationData.analytics_services)) {
        networkMonetizationData.analytics_services.forEach((serviceName: string) => {
         otherServices.push(serviceName);
       });
      }
    
         console.log(`✅ [MONETIZATION] Final results: ${adNetworks.length} ad networks, ${tagManagementServices.length} tag services, ${otherServices.length} analytics services`);
    
    // Parse search engine indexing data
    const searchIndexingData = domainNetworkInfo.search_engine_indexing || {};
    
    console.log('   - searchIndexingData:', searchIndexingData);
    
    // 🔧 Helper function to parse dates properly  
    const parseDate = (dateValue: any): string => {
      // Handle null, undefined, or empty values
      if (dateValue === null || dateValue === undefined || dateValue === 'null' || dateValue === '') {
        return 'Unknown';
      }
      
      try {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) {
          return 'Unknown';
        }
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return 'Unknown';
      }
    };
    
    // 🔧 Enhanced CDN detection
    const detectCDN = (nameservers: string[], organization: string, isp: string): string | null => {
      const cdnIndicators = {
        'cloudflare': 'CloudFlare',
        'amazonaws': 'AWS CloudFront', 
        'akamai': 'Akamai',
        'fastly': 'Fastly',
        'maxcdn': 'MaxCDN',
        'cloudfront': 'AWS CloudFront',
        'googlesyndication': 'Google CDN',
        'azure': 'Azure CDN',
        'sourcedns': 'SourceDNS (DNS Provider)',
        'liquidweb': 'Liquid Web Hosting'
      };
      
      console.log(`🔧 [CDN] Checking: nameservers=${nameservers.length}, org="${organization}", isp="${isp}"`);
      
      // Check nameservers first
      for (const ns of nameservers) {
        for (const [indicator, cdnName] of Object.entries(cdnIndicators)) {
          if (ns.toLowerCase().includes(indicator)) {
            console.log(`🔧 [CDN] Detected from nameserver: ${cdnName}`);
            return cdnName;
          }
        }
      }
      
      // Check organization/ISP
      const combinedText = `${organization} ${isp}`.toLowerCase();
      for (const [indicator, cdnName] of Object.entries(cdnIndicators)) {
        if (combinedText.includes(indicator)) {
          console.log(`🔧 [CDN] Detected from org/ISP: ${cdnName}`);
          return cdnName;
        }
      }
      
      console.log(`🔧 [CDN] No CDN detected - standard hosting`);
      return null;
    };
    
    // 🔧 Enhanced SSL detection from multiple sources
    const detectSSLStatus = (): string => {
      // Check search engine indexing data first (most reliable)
      const searchIndexing = domainNetworkInfo.search_engine_indexing || {};
      console.log(`🔧 [SSL] Checking search indexing URL: ${searchIndexing.url}`);
      
      if (searchIndexing.url) {
        if (searchIndexing.url.startsWith('https://')) {
          console.log(`🔧 [SSL] Detected: SSL Enabled (HTTPS)`);
          return 'SSL Enabled';
        } else if (searchIndexing.url.startsWith('http://')) {
          console.log(`🔧 [SSL] Detected: No SSL (HTTP only)`);
          return 'No SSL (HTTP only)';
        }
      }
      
      // Fallback: Check evidence URLs
      const evidenceUrls = data.evidence_urls || [];
      console.log(`🔧 [SSL] Fallback: Checking ${evidenceUrls.length} evidence URLs`);
      for (const urlEntry of evidenceUrls) {
        if (urlEntry.url && urlEntry.url.includes(hostname)) {
          if (urlEntry.url.startsWith('https://')) {
            console.log(`🔧 [SSL] Detected from evidence: SSL Enabled`);
            return 'SSL Enabled';
          } else if (urlEntry.url.startsWith('http://')) {
            console.log(`🔧 [SSL] Detected from evidence: No SSL (HTTP only)`);
            return 'No SSL (HTTP only)';
          }
        }
      }
      
      console.log(`🔧 [SSL] Unable to determine SSL status`);
      return 'Unknown';
    };

    // Calculate risk score based on available data
    const riskScore = this.calculateRiskScore(JSON.stringify(domainNetworkInfo));

    const finalAnalyticsObject = {
      googleAnalytics: null,
      googleTagManager: adNetworks.includes('Google Tag Manager') ? 'Detected' : null,
      facebookPixel: null,
      yandexMetrica: null,
      hotjar: null,
      other: []
    };



    const finalMonetizationObject = {
      paymentProcessors: [],
      cryptoAddresses: [],
      adNetworks: adNetworks,
      tagManagementServices: tagManagementServices,
      otherServices: otherServices,
      affiliatePrograms: [],
      ecommercePlatform: null,
             totalExternalServices: (networkMonetizationData.ad_networks_detected?.length || 0) + (networkMonetizationData.analytics_services?.length || 0),
       hasInternalMonetization: networkMonetizationData.primary_monetization_method !== 'Unknown'
    };

    

    return {
      url: `https://${hostname}`,
      hostname,
      domain: hostname,
      domainData: {
        registrar: whoisData.registrar || 'Unknown',
        registrationDate: parseDate(whoisData.creation_date) === 'Unknown' 
          ? `Data collected: ${parseDate(domainNetworkInfo.collection_timestamp)}`
          : parseDate(whoisData.creation_date),
        expirationDate: parseDate(whoisData.expiration_date),
        nameservers: nameservers.length > 0 ? nameservers : ['Not available via IP service - DNS lookup required'],
        abuseContact: this.extractAbuseContact(domainNetworkInfo.isp || domainNetworkInfo.organization || 'Unknown'),
        whoisPrivacy: whoisData.privacy_protected || false,
        registrant: whoisData.registrant || (whoisData.privacy_protected ? 'Privacy Protected' : 'Unknown')
      },
      hostingData: {
        ipAddress: domainNetworkInfo.ip_address || 'Unknown',
        asn: domainNetworkInfo.as_number || 'Unknown',
        isp: domainNetworkInfo.isp || domainNetworkInfo.organization || 'Unknown',
        country: enhancedLocation.country,
        city: `${enhancedLocation.city}, ${enhancedLocation.region}`,
        cdn: detectCDN(nameservers, domainNetworkInfo.organization || 'Unknown', domainNetworkInfo.isp || 'Unknown'),
        reverseDns: `${hostname}`,
        sslStatus: detectSSLStatus(),
        ports: [80, 443] // Default ports, could be enhanced
      },
      analytics: finalAnalyticsObject,
      monetization: finalMonetizationObject,
      outboundLinks: {
        total: 0,
        monetized: [],
        partnerships: [],
        suspicious: []
      },
      embeddedBrands: {
        logos: [],
        knownEntities: [],
        socialMedia: []
      },
      searchIndexing: {
        googleIndexed: searchIndexingData.google?.indexed || false,
        bingIndexed: searchIndexingData.bing?.indexed || false,
        yandexIndexed: searchIndexingData.yandex?.indexed || false,
        sitemap: null,
        robotsTxt: null,
        metaTitle: `${hostname}`,
        metaDescription: `Information about ${hostname}`
      },
      riskScore,
      lastUpdated: new Date()
    };
  }

  // DEPRECATED METHODS - keeping for backwards compatibility but they now use the single API call
  async getDomainsFromEvidence(caseName?: string): Promise<string[]> {
    console.log('⚠️ [Infrastructure Service] getDomainsFromEvidence is deprecated - use getAllInfrastructureData instead');
    const allData = await this.getAllInfrastructureData(caseName);
    return allData.map(data => data.domain);
  }

  async getInfrastructureForDomain(domain: string, caseName?: string): Promise<InfrastructureData> {
    console.log('⚠️ [Infrastructure Service] getInfrastructureForDomain is deprecated - use getAllInfrastructureData instead');
    const allData = await this.getAllInfrastructureData(caseName);
    const found = allData.find(data => data.domain === domain);
    return found || this.createDefaultInfrastructureData(domain);
  }

  /**
   * Calculate risk score based on response content
   */
  private calculateRiskScore(responseText: string): number {
    let score = 50; // Base score
    
    // Increase score for suspicious indicators
    if (responseText.toLowerCase().includes('privacy protected')) score += 10;
    if (responseText.toLowerCase().includes('proxy')) score += 15;
    if (responseText.toLowerCase().includes('bulletproof')) score += 20;
    if (responseText.toLowerCase().includes('offshore')) score += 15;
    
    // Decrease score for legitimate indicators
    if (responseText.toLowerCase().includes('cloudflare')) score -= 5;
    if (responseText.toLowerCase().includes('ssl')) score -= 5;
    
    return Math.min(Math.max(score, 0), 100);
  }

  /**
   * Extract abuse contact based on ISP/provider
   */
  private extractAbuseContact(isp: string): string {
    const contacts: Record<string, string> = {
      'CloudFlare': 'abuse@cloudflare.com',
      'Cloudflare': 'abuse@cloudflare.com',
      'Amazon': 'abuse@amazon.com',
      'Google': 'abuse@google.com',
      'Liquid Web': 'abuse@liquidweb.com',
      'GoDaddy': 'abuse@godaddy.com'
    };
    
    // Normalize ISP name for better matching
    const normalizedIsp = isp.toLowerCase().trim();
    
    for (const [provider, contact] of Object.entries(contacts)) {
      if (normalizedIsp.includes(provider.toLowerCase())) {
        return contact;
      }
    }
    
    // Better fallback: extract main domain name from ISP string
    // Handle cases like "GoDaddy.com, LLC" or "Amazon Web Services, Inc."
    let cleanIsp = isp.toLowerCase();
    
    // Remove common business suffixes
    cleanIsp = cleanIsp.replace(/,?\s*(llc|inc|corp|ltd|limited)\.?$/i, '');
    
    // Extract the main part before the first dot or space
    const mainPart = cleanIsp.split(/[.,\s]/)[0];
    
    // Clean up non-alphanumeric characters
    const cleanDomain = mainPart.replace(/[^a-z0-9]/g, '');
    
    if (cleanDomain) {
      return `abuse@${cleanDomain}.com`;
    }
    
    // Final fallback
    return 'abuse@unknown.com';
  }

  /**
   * Create default infrastructure data for fallback
   */
  private createDefaultInfrastructureData(domain: string): InfrastructureData {
    const hostname = domain.replace(/^https?:\/\//, '').replace(/^www\./, '');
    
    return {
      url: `https://${hostname}`,
      hostname,
      domain: hostname,
      domainData: {
        registrar: 'Unknown',
        registrationDate: 'Unknown',
        expirationDate: 'Unknown',
        nameservers: [],
        abuseContact: 'Unknown',
        whoisPrivacy: false,
        registrant: 'Unknown'
      },
      hostingData: {
        ipAddress: 'Unknown',
        asn: 'Unknown',
        isp: 'Unknown',
        country: 'Unknown',
        city: 'Unknown',
        cdn: null,
        reverseDns: hostname,
        sslStatus: 'Unknown',
        ports: [80, 443]
      },
      analytics: {
        googleAnalytics: null,
        googleTagManager: null,
        facebookPixel: null,
        yandexMetrica: null,
        hotjar: null,
        other: []
      },
      monetization: {
        paymentProcessors: [],
        cryptoAddresses: [],
        adNetworks: [],
        tagManagementServices: [],
        otherServices: [],
        affiliatePrograms: [],
        ecommercePlatform: null,
        totalExternalServices: 0,
        hasInternalMonetization: false
      },
      outboundLinks: {
        total: 0,
        monetized: [],
        partnerships: [],
        suspicious: []
      },
      embeddedBrands: {
        logos: [],
        knownEntities: [],
        socialMedia: []
      },
      searchIndexing: {
        googleIndexed: false,
        bingIndexed: false,
        yandexIndexed: false,
        sitemap: null,
        robotsTxt: null,
        metaTitle: hostname,
        metaDescription: `Information about ${hostname}`
      },
      riskScore: 50,
      lastUpdated: new Date()
    };
  }
}

// Create singleton instance
export const infrastructureService = new InfrastructureService('27june1341'); 